import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect.js";
import type { Client } from "@connectrpc/connect";
import { createClient } from "@connectrpc/connect";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { transport } from "./grpc";

export interface WorkspaceSettings {
	ai_model?: string;
	locale?: string;
	timezone?: string;
	[key: string]: unknown;
}

export interface Workspace {
	id: string;
	name: string;
	slug: string;
	avatarUrl: string;
	organizationId: string;
	settings: WorkspaceSettings;
	features: string[];
}

interface WorkspaceState {
	workspaces: Workspace[];
	selectedId: string | null;
	isLoading: boolean;
	error: string | null;
	selected: () => Workspace | undefined;
	setSelected: (id: string) => void;
	fetchWorkspaces: () => Promise<void>;
	createWorkspace: (name: string, slug: string, organizationId?: string) => Promise<Workspace>;
}

// Lazy-init to break circular dependency: workspace.ts ↔ grpc.ts
let _client: Client<typeof WorkspaceService> | null = null;
function getClient() {
	if (!_client) _client = createClient(WorkspaceService, transport);
	return _client;
}

export const useWorkspaceStore = create<WorkspaceState>()(
	persist(
		(set, get) => ({
			workspaces: [],
			selectedId: null,
			isLoading: false,
			error: null,

			selected: () => {
				const { workspaces, selectedId } = get();
				return workspaces.find((w) => w.id === selectedId);
			},

			setSelected: (id: string) => {
				set({ selectedId: id });
			},

			fetchWorkspaces: async () => {
				set({ isLoading: true, error: null });
				try {
					const resp = await getClient().listWorkspaces({});
					const workspaces = resp.workspaces.map((w) => ({
						id: w.id,
						name: w.name,
						slug: w.slug,
						avatarUrl: w.avatarUrl, organizationId: w.organizationId ?? "",
						settings: (w.settings?.toJson() ?? {}) as WorkspaceSettings,
						features: w.features ?? [],
					}));
					const { selectedId } = get();
					const still = workspaces.find((w) => w.id === selectedId);
					set({
						workspaces,
						isLoading: false,
						selectedId: still ? selectedId : (workspaces[0]?.id ?? null),
					});
				} catch (e: unknown) {
					set({
						isLoading: false,
						error: e instanceof Error ? e.message : "Failed to load workspaces",
					});
				}
			},

			createWorkspace: async (name: string, slug: string, organizationId?: string) => {
				const resp = await getClient().createWorkspace({
					name,
					slug,
					organizationId: organizationId ?? "",
				});
				const ws: Workspace = {
					id: resp.id,
					name: resp.name,
					slug: resp.slug,
					avatarUrl: resp.avatarUrl,
					organizationId: resp.organizationId ?? "",
					settings: (resp.settings?.toJson() ?? {}) as WorkspaceSettings,
					features: resp.features ?? [],
				};
				set((state) => ({
					workspaces: [...state.workspaces, ws],
					selectedId: ws.id,
				}));
				return ws;
			},
		}),
		{
			name: "omnios-workspace",
			partialize: (state) => ({
				selectedId: state.selectedId,
			}),
		},
	),
);
